/**
 * Report PDF builders — split free vs premium by design.
 *
 *   buildFreeReportPdf(sections)       returns a Blob with ONLY free sections.
 *                                       Premium fields are not present in the
 *                                       input shape, so there is no code path
 *                                       that could leak them.
 *
 *   buildPremiumReportPdf(sections)    returns a Blob containing all sections
 *                                       (free + premium). Only callable when
 *                                       the API has handed us a 'premium'
 *                                       sections payload.
 *
 *   uploadGeneratedPdf(reportId, ...)  POSTs the PDF bytes back to the
 *                                       Worker so it lands in Supabase
 *                                       Storage with a server-controlled
 *                                       object key.
 *
 * The PDF output is text-only (jsPDF) for reliability. Charts are
 * rendered as compact stat tables rather than rasterized canvases —
 * this keeps the file small and the content easy to read.
 */

import jsPDF from 'jspdf';
import type {
  FreeReportSections,
  PremiumReportSections,
  ReportSections,
  ReportVariant,
} from './reportsApi';
import { reportsApi } from './reportsApi';

// ── Layout constants ──────────────────────────────────────────────────────

const PAGE_W = 595;   // A4 portrait, points
const PAGE_H = 842;
const MARGIN = 48;
const CONTENT_W = PAGE_W - MARGIN * 2;

const COLOR_INK    = '#0E1116';
const COLOR_INK_2  = '#525866';
const COLOR_INK_3  = '#8A91A0';
const COLOR_ACCENT = '#5B5BD6';
const COLOR_BORDER = '#E5E7EB';
const COLOR_BG     = '#FAFAF7';

// ── Helpers ──────────────────────────────────────────────────────────────

interface PdfState {
  doc: jsPDF;
  y: number;
  page: number;
}

function newDoc(): PdfState {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  // Background tint the first page
  doc.setFillColor(COLOR_BG);
  doc.rect(0, 0, PAGE_W, PAGE_H, 'F');
  return { doc, y: MARGIN, page: 1 };
}

function newPage(s: PdfState): void {
  s.doc.addPage();
  s.doc.setFillColor(COLOR_BG);
  s.doc.rect(0, 0, PAGE_W, PAGE_H, 'F');
  s.y = MARGIN;
  s.page += 1;
  drawFooter(s);
}

function ensureSpace(s: PdfState, needed: number): void {
  if (s.y + needed > PAGE_H - MARGIN - 24) newPage(s);
}

function drawFooter(s: PdfState): void {
  s.doc.setFont('helvetica', 'normal');
  s.doc.setFontSize(8);
  s.doc.setTextColor(COLOR_INK_3);
  s.doc.text(
    `CreatorHub360 Report · Page ${s.page}`,
    PAGE_W / 2,
    PAGE_H - 24,
    { align: 'center' }
  );
}

function setInk(s: PdfState, color: string = COLOR_INK): void {
  s.doc.setTextColor(color);
}

function heading1(s: PdfState, text: string): void {
  ensureSpace(s, 60);
  s.doc.setFont('helvetica', 'bold');
  s.doc.setFontSize(22);
  setInk(s, COLOR_INK);
  s.doc.text(text, MARGIN, s.y);
  s.y += 30;
}

function eyebrow(s: PdfState, text: string): void {
  ensureSpace(s, 28);
  s.doc.setFont('helvetica', 'bold');
  s.doc.setFontSize(8);
  setInk(s, COLOR_ACCENT);
  s.doc.text(text.toUpperCase(), MARGIN, s.y, { charSpace: 1 });
  s.y += 14;
}

function heading2(s: PdfState, text: string): void {
  ensureSpace(s, 36);
  s.doc.setFont('helvetica', 'bold');
  s.doc.setFontSize(15);
  setInk(s, COLOR_INK);
  s.doc.text(text, MARGIN, s.y);
  s.y += 22;
}

function paragraph(s: PdfState, text: string, width: number = CONTENT_W): void {
  s.doc.setFont('helvetica', 'normal');
  s.doc.setFontSize(10);
  setInk(s, COLOR_INK_2);
  const lines = s.doc.splitTextToSize(text, width);
  ensureSpace(s, lines.length * 14 + 4);
  s.doc.text(lines, MARGIN, s.y);
  s.y += lines.length * 14 + 6;
}

function divider(s: PdfState): void {
  ensureSpace(s, 20);
  s.doc.setDrawColor(COLOR_BORDER);
  s.doc.setLineWidth(0.5);
  s.doc.line(MARGIN, s.y, PAGE_W - MARGIN, s.y);
  s.y += 16;
}

function kpiGrid(s: PdfState, items: Array<{ label: string; value: string }>, cols: number = 4): void {
  const cellW = CONTENT_W / cols;
  const cellH = 56;
  const rows = Math.ceil(items.length / cols);
  ensureSpace(s, rows * cellH + 8);

  for (let i = 0; i < items.length; i++) {
    const r = Math.floor(i / cols);
    const c = i % cols;
    const x = MARGIN + c * cellW;
    const y = s.y + r * cellH;
    s.doc.setDrawColor(COLOR_BORDER);
    s.doc.setFillColor(255, 255, 255);
    s.doc.roundedRect(x + 2, y + 2, cellW - 4, cellH - 8, 6, 6, 'FD');
    s.doc.setFont('helvetica', 'bold');
    s.doc.setFontSize(7);
    setInk(s, COLOR_INK_3);
    s.doc.text(items[i].label.toUpperCase(), x + 12, y + 18, { charSpace: 0.6 });
    s.doc.setFont('helvetica', 'bold');
    s.doc.setFontSize(13);
    setInk(s, COLOR_INK);
    s.doc.text(items[i].value, x + 12, y + 38);
  }
  s.y += rows * cellH + 4;
}

function table(s: PdfState, header: string[], rows: string[][], colWidths?: number[]): void {
  const widths =
    colWidths ?? header.map(() => CONTENT_W / header.length);
  const headerH = 26;
  const rowH = 22;
  ensureSpace(s, headerH + Math.min(rows.length, 8) * rowH);

  // Header bar
  s.doc.setFillColor(245, 245, 240);
  s.doc.rect(MARGIN, s.y, CONTENT_W, headerH, 'F');
  s.doc.setFont('helvetica', 'bold');
  s.doc.setFontSize(8);
  setInk(s, COLOR_INK_3);
  let x = MARGIN + 12;
  for (let i = 0; i < header.length; i++) {
    s.doc.text(header[i].toUpperCase(), x, s.y + 17, { charSpace: 0.6 });
    x += widths[i];
  }
  s.y += headerH;

  // Rows
  for (const row of rows) {
    ensureSpace(s, rowH);
    x = MARGIN + 12;
    s.doc.setFont('helvetica', 'normal');
    s.doc.setFontSize(10);
    setInk(s, COLOR_INK);
    for (let i = 0; i < row.length; i++) {
      const text = row[i];
      const lines = s.doc.splitTextToSize(text, widths[i] - 16);
      s.doc.text(lines[0] ?? '', x, s.y + 15);
      x += widths[i];
    }
    // Bottom border
    s.doc.setDrawColor(COLOR_BORDER);
    s.doc.setLineWidth(0.5);
    s.doc.line(MARGIN, s.y + rowH, PAGE_W - MARGIN, s.y + rowH);
    s.y += rowH;
  }
  s.y += 8;
}

function bulletList(s: PdfState, items: string[]): void {
  s.doc.setFont('helvetica', 'normal');
  s.doc.setFontSize(10);
  setInk(s, COLOR_INK);
  for (const it of items) {
    const lines = s.doc.splitTextToSize(it, CONTENT_W - 16);
    ensureSpace(s, lines.length * 14 + 4);
    s.doc.setFillColor(COLOR_ACCENT);
    s.doc.circle(MARGIN + 4, s.y + 5, 1.6, 'F');
    s.doc.text(lines, MARGIN + 16, s.y + 8);
    s.y += lines.length * 14 + 6;
  }
}

function fmtNumber(n: number | null | undefined): string {
  if (n == null) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}
function fmtPct(v: number | null | undefined, d = 1): string {
  if (v == null) return '—';
  return `${(v * 100).toFixed(d)}%`;
}
function fmtUsd(n: number | null | undefined): string {
  if (n == null) return '—';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${n.toLocaleString()}`;
}

// ── Cover page ──────────────────────────────────────────────────────────

function drawCover(s: PdfState, sec: FreeReportSections | PremiumReportSections, variant: ReportVariant): void {
  // Big block
  s.doc.setFillColor(COLOR_INK);
  s.doc.rect(0, 0, PAGE_W, 280, 'F');

  s.doc.setFont('helvetica', 'bold');
  s.doc.setFontSize(10);
  s.doc.setTextColor('#FFD400');
  s.doc.text('CREATORHUB360 REPORT', MARGIN, 80, { charSpace: 1.4 });

  s.doc.setFontSize(28);
  s.doc.setTextColor('#FFFFFF');
  const title = `@${sec.snapshot.handle}`;
  s.doc.text(title, MARGIN, 130);

  s.doc.setFont('helvetica', 'normal');
  s.doc.setFontSize(12);
  s.doc.setTextColor('#FFFFFF');
  s.doc.text(`${sec.snapshot.platform} · ${sec.snapshot.primary_category ?? 'Multi-vertical creator'}`, MARGIN, 156);

  // Report meta
  s.doc.setFontSize(9);
  s.doc.setTextColor('#9CA3AF');
  const meta = [
    `Variant: ${variant.toUpperCase()}`,
    `Generated: ${new Date().toLocaleDateString()}`,
  ];
  s.doc.text(meta.join('  ·  '), MARGIN, 180);

  // Hero stats strip
  s.y = 320;
  kpiGrid(s, [
    { label: 'Followers',    value: fmtNumber(sec.snapshot.followers) },
    { label: 'Engagement',   value: fmtPct(sec.snapshot.engagement_rate) },
    { label: 'Avg views',    value: fmtNumber(sec.snapshot.avg_views) },
    { label: 'Avg likes',    value: fmtNumber(sec.snapshot.avg_likes) },
  ], 4);

  paragraph(s, sec.snapshot.niche_summary);
  paragraph(s, sec.snapshot.positioning);
  s.y += 20;
  drawFooter(s);
  newPage(s);
}

// ── Sections ────────────────────────────────────────────────────────────

// Shared fields available on BOTH free and premium reports. Used by writers
// that only touch the common subset.
type SharedReportSections = Omit<FreeReportSections, 'variant'>;

function writeSnapshot(s: PdfState, sec: SharedReportSections): void {
  eyebrow(s, '01 — Creator Snapshot');
  heading1(s, 'Who you are at a glance');
  paragraph(s, sec.snapshot.niche_summary);
  paragraph(s, sec.snapshot.positioning);
  divider(s);
  kpiGrid(s, [
    { label: 'Followers',  value: fmtNumber(sec.snapshot.followers) },
    { label: 'Engagement', value: fmtPct(sec.snapshot.engagement_rate) },
    { label: 'Avg views',  value: fmtNumber(sec.snapshot.avg_views) },
    { label: 'Avg likes',  value: fmtNumber(sec.snapshot.avg_likes) },
    { label: 'Platform',   value: sec.snapshot.platform },
    { label: 'Niche',      value: sec.snapshot.primary_category ?? 'Multi' },
    { label: 'Posts',      value: fmtNumber(sec.snapshot.posts_count) },
    { label: 'Verticals',  value: String((sec.snapshot.secondary_categories?.length ?? 0) + (sec.snapshot.primary_category ? 1 : 0)) },
  ]);
  s.y += 12;
}

function writeAudience(s: PdfState, sec: SharedReportSections): void {
  ensureSpace(s, 100);
  eyebrow(s, '02 — Audience Intelligence');
  heading1(s, 'Who follows you');

  const a = sec.audience;
  const male = a.gender_split.male ?? 0;
  const female = a.gender_split.female ?? 0;

  kpiGrid(s, [
    { label: 'Female %',     value: fmtPct(female) },
    { label: 'Male %',       value: fmtPct(male) },
    { label: 'Credibility',  value: a.credibility_score != null ? `${Math.round(a.credibility_score * 100)}%` : '—' },
    { label: 'Reachability', value: a.reachability_label ?? '—' },
  ]);

  if (a.top_countries && a.top_countries.length > 0) {
    heading2(s, 'Top countries');
    table(
      s,
      ['Country', 'Audience share'],
      a.top_countries.slice(0, 8).map((c) => [c.label, fmtPct(c.followers_pct)]),
      [CONTENT_W * 0.7, CONTENT_W * 0.3]
    );
  }

  if (a.audience_interests && a.audience_interests.length > 0) {
    heading2(s, 'Audience interests');
    table(
      s,
      ['Interest', 'Audience share'],
      a.audience_interests.slice(0, 8).map((i) => [i.label, fmtPct(i.followers_pct)]),
      [CONTENT_W * 0.7, CONTENT_W * 0.3]
    );
  }
  if (a.brand_affinity && a.brand_affinity.length > 0) {
    heading2(s, 'Brand affinity');
    table(
      s,
      ['Brand', 'Audience share'],
      a.brand_affinity.slice(0, 8).map((b) => [b.label, fmtPct(b.followers_pct)]),
      [CONTENT_W * 0.7, CONTENT_W * 0.3]
    );
  }
}

function writeCohort(s: PdfState, sec: SharedReportSections): void {
  eyebrow(s, '03 — Cohort Benchmark');
  heading1(s, 'How you compare to peers');
  paragraph(s, `Cohort: ${sec.cohort.cohort_label}`);
  divider(s);
  kpiGrid(s, [
    { label: 'Income low',    value: fmtUsd(sec.cohort.income_low) + '/mo' },
    { label: 'Income avg',    value: fmtUsd(sec.cohort.income_avg) + '/mo' },
    { label: 'Income high',   value: fmtUsd(sec.cohort.income_high) + '/mo' },
    { label: 'Sample size',   value: String(sec.cohort.sample_size) },
  ]);
  if (sec.cohort.your_engagement != null) {
    kpiGrid(s, [
      { label: 'Your engagement', value: fmtPct(sec.cohort.your_engagement) },
      { label: 'Cohort p50',      value: fmtPct(sec.cohort.engagement_p50) },
      { label: 'Cohort p90',      value: fmtPct(sec.cohort.engagement_p90) },
      { label: 'Your percentile', value: sec.cohort.your_percentile != null ? `${sec.cohort.your_percentile}%` : '—' },
    ]);
  }
}

function writeMonetizationOverview(s: PdfState, sec: SharedReportSections): void {
  eyebrow(s, '04 — Monetization Opportunities');
  heading1(s, 'What you could earn');
  const m = sec.monetization_overview;
  kpiGrid(s, [
    { label: 'Conservative', value: fmtUsd(m.total_estimated_low) },
    { label: 'Expected',     value: fmtUsd(m.total_estimated_expected) },
    { label: 'Stretch',      value: fmtUsd(m.total_estimated_high) },
    { label: 'Tier',         value: m.tier ? `Tier ${m.tier}` : '—' },
  ]);

  heading2(s, 'Estimated revenue mix');
  table(
    s,
    ['Stream', 'Low', 'Expected', 'High'],
    m.mix.map((c) => [
      `${c.label}${c.locked ? ' (Premium)' : ''}`,
      fmtUsd(c.low),
      fmtUsd(c.expected),
      fmtUsd(c.high),
    ]),
    [CONTENT_W * 0.4, CONTENT_W * 0.2, CONTENT_W * 0.2, CONTENT_W * 0.2]
  );

  heading2(s, 'What CreatorHub unlocks');
  bulletList(s, m.what_creatorhub_unlocks);
}

function writeContentSignals(s: PdfState, sec: SharedReportSections, isPremium: boolean): void {
  eyebrow(s, '05 — Content & Performance');
  heading1(s, 'How your content performs');
  if (sec.content_signals.posting_cadence) {
    paragraph(s, sec.content_signals.posting_cadence);
  }
  if (sec.content_signals.format_breakdown.length > 0) {
    heading2(s, 'Format mix');
    table(
      s,
      ['Format', 'Share'],
      sec.content_signals.format_breakdown.map((f) => [f.name, `${f.value}%`]),
      [CONTENT_W * 0.7, CONTENT_W * 0.3]
    );
  }
  if (isPremium && sec.content_signals.popular_hashtags.length > 0) {
    heading2(s, 'Popular hashtags');
    paragraph(
      s,
      sec.content_signals.popular_hashtags.map((h) => `#${h.tag} (${(h.pct * 100).toFixed(1)}%)`).join('   ·   ')
    );
  }
}

function writeActionsBasic(s: PdfState, sec: FreeReportSections): void {
  eyebrow(s, '08 — Recommended Actions');
  heading1(s, 'Your monetization playbook');
  for (const a of sec.action_plan_basic) {
    ensureSpace(s, 56);
    s.doc.setFont('helvetica', 'bold');
    s.doc.setFontSize(11);
    setInk(s, COLOR_INK);
    s.doc.text(`${a.rank}. ${a.title}`, MARGIN, s.y);
    s.y += 16;
    paragraph(s, a.description);
    s.doc.setFont('helvetica', 'bold');
    s.doc.setFontSize(8);
    setInk(s, COLOR_INK_3);
    s.doc.text(`Effort: ${a.effort.toUpperCase()}   ·   Impact: ${a.impact.toUpperCase()}`, MARGIN, s.y, { charSpace: 0.4 });
    s.y += 18;
  }
}

function writePremiumPreviewCta(s: PdfState, sec: FreeReportSections): void {
  newPage(s);
  s.doc.setFillColor(COLOR_INK);
  s.doc.rect(MARGIN - 8, s.y - 8, CONTENT_W + 16, 200, 'F');
  s.doc.setFont('helvetica', 'bold');
  s.doc.setFontSize(8);
  s.doc.setTextColor('#FFD400');
  s.doc.text('PREMIUM', MARGIN + 16, s.y + 24, { charSpace: 1.4 });
  s.doc.setFont('helvetica', 'bold');
  s.doc.setFontSize(20);
  s.doc.setTextColor('#FFFFFF');
  s.doc.text('Unlock the full playbook', MARGIN + 16, s.y + 56);
  s.doc.setFont('helvetica', 'normal');
  s.doc.setFontSize(11);
  s.doc.setTextColor('#D1D5DB');
  const lines = s.doc.splitTextToSize(
    'Pro and Scale plans add product matching, vendor connections, brand deal pipeline, posting timing intelligence, and the full action plan tailored to your audience.',
    CONTENT_W - 16
  );
  s.doc.text(lines, MARGIN + 16, s.y + 80);

  let yy = s.y + 134;
  s.doc.setFontSize(10);
  s.doc.setTextColor('#FFFFFF');
  for (const b of sec.premium_preview.bullets.slice(0, 4)) {
    s.doc.text(`•  ${b}`, MARGIN + 16, yy);
    yy += 14;
  }

  s.y += 220;
}

// ── Premium-only sections ──────────────────────────────────────────────

function writeProducts(s: PdfState, sec: PremiumReportSections): void {
  eyebrow(s, '06 — Product Matches');
  heading1(s, 'Top product matches');
  table(
    s,
    ['Rank', 'Product', 'Vertical', 'Score', 'Est. GMV'],
    sec.product_matches.map((p) => [
      `#${p.rank}`,
      p.title,
      `${p.vertical} / ${p.category}`,
      `${Math.round(p.score * 100)}`,
      p.gmv_expected != null ? fmtUsd(p.gmv_expected) : '—',
    ]),
    [CONTENT_W * 0.08, CONTENT_W * 0.42, CONTENT_W * 0.22, CONTENT_W * 0.10, CONTENT_W * 0.18]
  );
  for (const p of sec.product_matches.slice(0, 5)) {
    paragraph(s, `#${p.rank} ${p.title} — ${p.why}`);
  }
}

function writeVendors(s: PdfState, sec: PremiumReportSections): void {
  eyebrow(s, '07 — Vendor Matches');
  heading1(s, 'Vendors aligned to your audience');
  table(
    s,
    ['Vendor', 'Category', 'Fit', 'Commission'],
    sec.vendor_matches.map((v) => [
      v.name,
      v.category,
      `${Math.round(v.fit_score * 100)}`,
      `${v.commission_band.low}–${v.commission_band.high}%`,
    ]),
    [CONTENT_W * 0.4, CONTENT_W * 0.3, CONTENT_W * 0.1, CONTENT_W * 0.2]
  );

  if (sec.brand_deal_opportunities.length > 0) {
    heading2(s, 'Brand-deal pipeline');
    table(
      s,
      ['Brand', 'Category', 'Fit', 'Estimated value'],
      sec.brand_deal_opportunities.map((b) => [
        b.brand_name,
        b.category,
        `${Math.round(b.fit_score * 100)}`,
        `$${b.estimated_value_low}–$${b.estimated_value_high}`,
      ]),
      [CONTENT_W * 0.3, CONTENT_W * 0.3, CONTENT_W * 0.1, CONTENT_W * 0.3]
    );
  }
}

function writeMonetizationFull(s: PdfState, sec: PremiumReportSections): void {
  heading2(s, 'Per-category monthly breakdown');
  table(
    s,
    ['Category', 'Monthly low', 'Monthly expected', 'Monthly high'],
    sec.monetization_full.by_category_breakdown.map((r) => [
      r.category,
      fmtUsd(r.monthly_low),
      fmtUsd(r.monthly_expected),
      fmtUsd(r.monthly_high),
    ]),
    [CONTENT_W * 0.4, CONTENT_W * 0.2, CONTENT_W * 0.2, CONTENT_W * 0.2]
  );
}

function writeActionsFull(s: PdfState, sec: PremiumReportSections): void {
  eyebrow(s, '08 — Recommended Actions');
  heading1(s, 'Your full monetization playbook');
  for (const a of sec.action_plan_full) {
    ensureSpace(s, 60);
    s.doc.setFont('helvetica', 'bold');
    s.doc.setFontSize(11);
    setInk(s, COLOR_INK);
    s.doc.text(`${a.rank}. ${a.title}${a.premium ? '  (Premium)' : ''}`, MARGIN, s.y);
    s.y += 16;
    paragraph(s, a.description);
    s.doc.setFont('helvetica', 'bold');
    s.doc.setFontSize(8);
    setInk(s, COLOR_INK_3);
    s.doc.text(`Effort: ${a.effort.toUpperCase()}   ·   Impact: ${a.impact.toUpperCase()}`, MARGIN, s.y, { charSpace: 0.4 });
    s.y += 18;
  }
}

// ── Public builders ─────────────────────────────────────────────────────

/**
 * Build a PDF for a *free* report. The input shape lacks premium
 * fields, so there is no code path that could include premium data.
 */
export function buildFreeReportPdf(sections: FreeReportSections): Blob {
  const s = newDoc();
  drawFooter(s);
  drawCover(s, sections, 'free');
  writeSnapshot(s, sections);
  writeAudience(s, sections);
  writeCohort(s, sections);
  writeMonetizationOverview(s, sections);
  writeContentSignals(s, sections, false);
  writeActionsBasic(s, sections);
  writePremiumPreviewCta(s, sections);
  return s.doc.output('blob');
}

/**
 * Build a PDF for a *premium* report. Includes all sections.
 */
export function buildPremiumReportPdf(sections: PremiumReportSections): Blob {
  const s = newDoc();
  drawFooter(s);
  drawCover(s, sections, 'premium');
  writeSnapshot(s, sections);
  writeAudience(s, sections);
  writeCohort(s, sections);
  writeMonetizationOverview(s, sections);
  writeMonetizationFull(s, sections);
  writeContentSignals(s, sections, true);
  writeProducts(s, sections);
  writeVendors(s, sections);
  writeActionsFull(s, sections);
  return s.doc.output('blob');
}

/**
 * Convenience: build the right PDF based on the variant of `sections`.
 */
export function buildReportPdf(sections: ReportSections): Blob {
  return sections.variant === 'premium'
    ? buildPremiumReportPdf(sections)
    : buildFreeReportPdf(sections);
}

/**
 * Render the PDF locally and POST the bytes to the API so the file
 * lands in Supabase Storage with a server-controlled object key.
 */
export async function renderAndUploadReportPdf(
  reportId: number,
  sections: ReportSections
): Promise<{ object_key: string; download_url: string }> {
  const blob = buildReportPdf(sections);
  const buffer = await blob.arrayBuffer();
  await reportsApi.uploadPdf(reportId, sections.variant, buffer);
  const dl = await reportsApi.download(reportId, sections.variant);
  return { object_key: `reports/${reportId}/${sections.variant}.pdf`, download_url: dl.data.url };
}

/**
 * Trigger a browser download of the PDF without going through Storage.
 * Useful for "instant download" UX during preview.
 */
export function downloadReportPdfLocal(sections: ReportSections, fileName?: string): void {
  const blob = buildReportPdf(sections);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName ?? `creatorhub-${sections.snapshot.handle}-${sections.variant}.pdf`;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }, 100);
}
